import {
  type GlobalRolePermissions,
  type MspPortalPermissions,
  type MspSpecialPermissions,
  type NestedGlobalPermissions,
  type NestedMspPermissions,
} from "@/_store/roles.module";
import { RolePermissionAccessMode } from "@/constants/roles";
import { toRaw } from "vue";
import { isGlobalAccessRestricted, isGlobalActionRestricted } from "@/_helpers/global-permissions";
import get from "lodash/get";
import { usePermissionsStore } from "@/_store/permissions.module.ts";

/**
 * Returns true when an MSP-scoped action is blocked. When no MSP permission entry exists at
 * `[scopeName][key]`, falls back to `isGlobalActionRestricted` so global role permissions still apply.
 * Otherwise resolves the entry like the global variant: string must equal `EDIT`, a special-permissions
 * object must have the `nestedPermissionKey` flag true, or a nested object must be in `EDIT` mode
 * (and, if a key is given, the matching `editAccessModePermission` flag must be true).
 * @param scopeName
 * @param key
 * @param nestedPermissionKey
 */
export function isMspActionRestricted<
  T extends keyof MspPortalPermissions,
  K extends keyof MspPortalPermissions[T],
  V extends (MspPortalPermissions[T][K] extends NestedMspPermissions
    ? NonNullable<MspPortalPermissions[T][K]["editAccessModePermission"]>
    : MspPortalPermissions[T][K]),
>(scopeName: T, key: NonNullable<K>, nestedPermissionKey?: keyof V) {
  const permissions = getMspPermissionsByScopeNameAndKey(scopeName, key);
  // Filter case when permissions doesn't exist
  if (!permissions) {
    return isGlobalActionRestricted(
      scopeName,
      key,
      nestedPermissionKey as keyof (GlobalRolePermissions[T][K] extends NestedGlobalPermissions
        ? NonNullable<GlobalRolePermissions[T][K]["editAccessModePermission"]>
        : GlobalRolePermissions[T][K])
    );
  }
  const isSpecialPermissions = (
    permission: RolePermissionAccessMode | NestedMspPermissions | MspSpecialPermissions
  ): permission is MspSpecialPermissions => {
    if (typeof permissions !== "object") return false;
    return Object.values(permission).every((val) => typeof val === "boolean");
  };

  // Filter case when permissions is just string
  if (typeof permissions === "string") {
    return permissions !== RolePermissionAccessMode.EDIT;
  }

  if (isSpecialPermissions(permissions)) {
    return !permissions[nestedPermissionKey as keyof MspSpecialPermissions];
  }

  // Filter case when permissions is an object of such shape
  // {
  //   accessMode: RolePermissionAccessMode;
  //   editAccessModePermission?: {[key: string]: boolean}
  // };

  if (typeof permissions === "object") {
    // If no key provided, then check if it's edit or not
    if (!nestedPermissionKey) {
      return permissions.accessMode !== RolePermissionAccessMode.EDIT;
    }

    // If key is provided, then check the particular action
    if (
      nestedPermissionKey &&
      permissions.accessMode === RolePermissionAccessMode.EDIT &&
      permissions.editAccessModePermission
    ) {
      // Indexing into a union of edit-permission shapes — TS can't narrow `keyof V` across the
      // union here. The structural cast to `Record<string, boolean>` matches what each variant
      // actually is at runtime.
      return permissions.editAccessModePermission
        ? !(permissions.editAccessModePermission as unknown as Record<string, boolean>)[
            nestedPermissionKey as string
          ]
        : true;
    }
  }

  return true;
}

/**
 * Returns true when the MSP permission entry resolves to `NO_ACCESS`. When no MSP entry exists,
 * defers to `isGlobalAccessRestricted` so global permissions take effect; safe-defaults to `true`
 * if a nested entry has no `accessMode` field.
 * @param scopeName
 * @param key
 */
export function isMspAccessRestricted<
  T extends keyof MspPortalPermissions = keyof MspPortalPermissions,
  K extends keyof MspPortalPermissions[T] = keyof MspPortalPermissions[T],
>(scopeName: T, key: K) {
  const permissions = getMspPermissionsByScopeNameAndKey(scopeName, key);
  if (!permissions) return isGlobalAccessRestricted(scopeName, key);

  if (typeof permissions === "string") {
    return permissions === RolePermissionAccessMode.NO_ACCESS;
  }
  return (permissions as NestedMspPermissions).accessMode
    ? (permissions as NestedMspPermissions).accessMode === RolePermissionAccessMode.NO_ACCESS
    : true;
}

/**
 * Reads the raw MSP permission entry at `[scopeName][key]` from the permissions store
 * (unwrapped via `toRaw` before `lodash/get`). Returns `undefined` when the MSP portal isn't
 * configured for the account or the scope/key pair is absent — callers use that to fall back
 * to global permissions.
 * @param scopeName
 * @param key
 */
function getMspPermissionsByScopeNameAndKey<
  T extends keyof MspPortalPermissions = keyof MspPortalPermissions,
  K extends keyof MspPortalPermissions[T] = keyof MspPortalPermissions[T],
>(scopeName: T, key: K) {
  const permissionsStore = usePermissionsStore();
  const mspPermissions: MspPortalPermissions | null = toRaw(permissionsStore.mspPortalPermissions);
  return get(mspPermissions, [scopeName, key]) as
    NestedMspPermissions | MspSpecialPermissions | RolePermissionAccessMode | undefined;
}
